import { debounce, minWidthQuery, getObserver, layoutRecalcManager } from '@theme/theme-global';

class ShowcaseCard extends HTMLElement {
  constructor() {
    super();

    this.handleScroll = this.handleScroll.bind(this);
    this.onResize = debounce(this.onResize.bind(this), 200);
    this.recalculateLayout = this.recalculateLayout.bind(this);
    this.mediaQuery = minWidthQuery(1025);
    this.ticking = false;
    this.resizeRaf = null;
    this.stickyStart = 0;
    this.activeSlide = -1;
    this._isDesktopSetup = false;
    this._layoutReady = false;
    this._sliderHeightsCached = false;
    this._lastLayoutWidth = null;
    this._lastLayoutHeight = null;
    this._lastMeasureWidth = null;
    this._lastWrapperHeight = null;
    this._lastCardHeight = null;
    this._fontsInvalidateBound = false;
  }

  connectedCallback() {
    const init = () => {
      this.wrapper = this.closest('[data-showcase-collections]');
      if (!this.wrapper) return;

      this.hero = this.wrapper.querySelector('[data-showcase-banner]');

      this.mediaCards = [...this.querySelectorAll('[showcase-card-image]')];
      this.images = this.mediaCards.map((card) => card.querySelector('img'));

      this.collectionItems = [...this.querySelectorAll('[showcase-card-info-item]')];
      this.tabWrapper = this.wrapper.querySelector('.showcase-collections-tabs');
      this.tabInner = this.wrapper.querySelector('[data-showcase-collections-tabs-inner]');
      this.tabItems = [...this.querySelectorAll('[data-showcase-collections-tab-item]')];
      this.infoList = this.querySelector('[showcase-card-info-list]');
      this.infoItems = [...this.querySelectorAll('[showcase-card-info-item]')];
      this.cardInner = this.querySelector('[data-showcase-card-inner]');
      this.cardContents = this.cardInner ? [...this.cardInner.querySelectorAll('[showcase-card-image-content]')] : [];
      this.totalSlides = this.mediaCards.length;
      if (!this.totalSlides) return;

      this.animationType = this.dataset.animationType || 'scroll';

      this.bindTabClicks();
      if (this.animationType === 'hover') {
        this.bindHoverEvents();
      }

      window.addEventListener('resize', this.onResize);

      if (this.mediaQuery.matches) {
        this.setupDesktop();
      }
    };
    if (this.mediaQuery.matches) {
      this._observer = getObserver({ rootMargin: '400px 0px', threshold: 0 }, true, false);
      this._observer.observe(this, () => {
        this.onResize();
      });
    }

    // Wait for full page load so fonts/images don't shift layout after measurement
    if (document.readyState === 'complete') {
      init();
    } else {
      window.addEventListener('load', init, { once: true });
    }
  }

  setupDesktop() {
    if (this._isDesktopSetup) return;
    this._isDesktopSetup = true;

    // Start transparent — avoid CSS opacity fallback (1) before first measured onScroll
    if (this.hero) this.hero.style.setProperty('--heroBeforeOpacity', 0);

    this.applyPerformanceHints();
    window.addEventListener('scroll', this.handleScroll, { passive: true });

    layoutRecalcManager.register(this.recalculateLayout, { root: this });
    this.bindFontsInvalidate();
    layoutRecalcManager.request(this.recalculateLayout);
  }

  bindFontsInvalidate() {
    if (this._fontsInvalidateBound) return;
    if (!document.fonts || document.fonts.status === 'loaded') return;

    this._fontsInvalidateBound = true;
    document.fonts.ready.then(() => {
      if (!this._isDesktopSetup) return;
      this._layoutReady = false;
      this._sliderHeightsCached = false;
      layoutRecalcManager.request(this.recalculateLayout);
    });
  }

  recalculateLayout(phase) {
    // Unconstrain writes cannot run in the global measure pass.
    if (phase === 'measure') return;
    if (!this._isDesktopSetup || !this.mediaCards) return;

    const width = window.innerWidth;
    const vh = window.innerHeight;
    const sizeChanged = this._lastLayoutWidth !== width || this._lastLayoutHeight !== vh;
    const needsMeasure = !this._layoutReady || sizeChanged;

    if (needsMeasure) {
      this._lastLayoutWidth = width;
      this._lastLayoutHeight = vh;
      this.cacheStickyStart();
      this.cacheSliderHeights();
    }

    this.setDynamicHeight();

    if (!needsMeasure) {
      this.updateCardHeightVariable();
      return;
    }

    this._layoutReady = true;

    if (this.animationType === 'hover') {
      if (this.activeSlide === -1) {
        this.activeSlide = 0;
      }
      this.updateCardHeightVariable();
      this.updateActiveStates();
    } else {
      this.activeSlide = -2;
      this.onScroll();
    }
  }

  teardownDesktop() {
    if (!this._isDesktopSetup) return;
    this._isDesktopSetup = false;

    layoutRecalcManager.unregister(this.recalculateLayout);
    window.removeEventListener('scroll', this.handleScroll);
    this.resetMobileStyles();
    this._layoutReady = false;
    this._sliderHeightsCached = false;
    this._lastLayoutWidth = null;
    this._lastLayoutHeight = null;
    this._lastMeasureWidth = null;
    this._lastWrapperHeight = null;
    this._lastCardHeight = null;
  }

  resetMobileStyles() {
    if (this.wrapper) this.wrapper.style.height = '';
    if (this.hero) this.hero.style.setProperty('--heroBeforeOpacity', '');

    this.mediaCards.forEach((slide) => {
      slide.style.clipPath = '';
      slide.style.willChange = '';
      slide.style.transform = '';
      slide.style.backfaceVisibility = '';
      slide.style.contain = '';
    });

    this.images.forEach((img) => {
      if (!img) return;
      img.style.willChange = '';
      img.style.transform = '';
      img.style.backfaceVisibility = '';
    });

    if (this.infoList) {
      this.infoList.style.willChange = '';
      this.infoList.style.transform = '';
    }

    if (this.cardInner) {
      this.cardInner.style.willChange = '';
      this.cardInner.style.transform = '';
      if (this.cardInner.parentElement) {
        this.cardInner.parentElement.style.setProperty('--showcaseCardHeight', '');
        this._lastCardHeight = null;
      }
    }

    this.collectionItems.forEach((el) => el.classList.remove('active'));
    this.tabItems.forEach((el) => el.classList.remove('active-tab'));
    this.cardContents.forEach((el) => el.classList.remove('active-card'));
    this.activeSlide = -1;
  }

  applyPerformanceHints() {
    this.mediaCards.forEach((slide) => {
      slide.style.willChange = 'clip-path';
      slide.style.transform = 'translate3d(0,0,0)';
      slide.style.backfaceVisibility = 'hidden';
      slide.style.contain = 'layout paint';
    });

    this.images.forEach((img) => {
      if (!img) return;
      img.style.willChange = 'transform';
      img.style.transform = 'translate3d(0,0,0)';
      img.style.backfaceVisibility = 'hidden';
    });

    if (this.infoList) {
      this.infoList.style.willChange = 'transform';
      this.infoList.style.transform = 'translate3d(0,0,0)';
    }

    if (this.cardInner) {
      this.cardInner.style.willChange = 'transform';
      this.cardInner.style.transform = 'translate3d(0,0,0)';
    }
  }

  /* ------------------------------- */

  cacheStickyStart() {
    // Get wrapper's absolute top from page using getBoundingClientRect + scrollY.
    // Called after full layout is settled (double-rAF after load), so scrollY is stable.
    const wrapperRect = this.wrapper.getBoundingClientRect();
    const wrapperTop = wrapperRect.top + window.scrollY;

    // showcase-card starts after the hero — use hero's offsetHeight as offset within wrapper
    const heroHeight = this.hero ? this.hero.offsetHeight : 0;

    const stickyOffset = parseFloat(getComputedStyle(this).top) || 0;
    this.stickyStart = wrapperTop + heroHeight - stickyOffset;
  }

  setDynamicHeight() {
    if (this.animationType === 'hover') {
      if (this._lastWrapperHeight === '') return;
      this.wrapper.style.height = '';
      this._lastWrapperHeight = '';
      return;
    }
    const vh = window.innerHeight;
    // Extra 0.5vh buffer so the last slide eases in smoothly
    const height = `${vh * this.totalSlides + vh * 1.5}px`;
    if (this._lastWrapperHeight === height) return;
    this._lastWrapperHeight = height;
    this.wrapper.style.height = height;
  }

  /* ------------------------------- */

  handleScroll() {
    if (!this.ticking) {
      this.ticking = true;
      requestAnimationFrame(() => {
        try {
          this.onScroll();
        } finally {
          this.ticking = false;
        }
      });
    }
  }
  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
  onScroll() {
    if (!this.mediaQuery.matches) return;
    const vh = window.innerHeight;
    // Match the 0.5vh buffer from setDynamicHeight so last slide fully opens
    const totalHeight = vh * this.totalSlides + vh * 0.1;

    let scrolled = window.scrollY - this.stickyStart;
    scrolled = Math.max(0, Math.min(scrolled, totalHeight));

    // --- Hero fade: starts when card approaches viewport, completes just before sticky ---
    // Guard: stickyStart is 0 until cacheStickyStart runs; without this, page-top scroll
    // treats fadeStart as negative and clamps opacity to 1 prematurely.
    if (this.hero) {
      let opacity = 0;
      if (this.stickyStart > 0) {
        const fadeStart = this.stickyStart - vh * 0.6;
        const fadeEnd = this.stickyStart - vh * 0.1;
        const fadeRange = fadeEnd - fadeStart;
        if (fadeRange > 0) {
          opacity = this.clamp((window.scrollY - fadeStart) / fadeRange, 0, 1);
        }
      }
      this.hero.style.setProperty('--heroBeforeOpacity', opacity);
    }

    if (this.animationType === 'hover') return;

    for (let index = 1; index < this.totalSlides; index++) {
      const slide = this.mediaCards[index];
      const img = this.images[index];

      const slideStart = vh * (index - 1);
      const slideEnd = vh * index;

      let reveal = vh;
      let radius = 10;
      let imgMove = 8;

      if (scrolled >= slideStart && scrolled <= slideEnd) {
        const progress = (scrolled - slideStart) / vh;

        reveal = vh * (1 - progress);
        radius = 10 + progress * 8;
        imgMove = 8 - progress * 8;
      } else if (scrolled > slideEnd) {
        reveal = 0;
        radius = 10;
        imgMove = 0;
      }

      slide.style.clipPath = `inset(${reveal}px 0 0)`;

      if (img) {
        img.style.transform = `translate3d(0, ${imgMove}%, 0)`;
      }
    }

    const newActiveSlide = Math.min(Math.floor(scrolled / vh), this.totalSlides - 1);

    // Agar Tab se navigate ho raha hai toh scroll event active state na badley
    if (!this._focusNavigating && newActiveSlide !== this.activeSlide) {
      this.activeSlide = newActiveSlide;
      this.updateActiveStates();
      this.updateCardHeightVariable();
    }
  }

  updateActiveStates() {
    const toggle = (elements, className) => {
      elements.forEach((el, i) => {
        el.classList.toggle(className, i === this.activeSlide);
      });
    };

    toggle(this.collectionItems, 'active');
    toggle(this.tabItems, 'active-tab');
    toggle(this.cardContents, 'active-card');

    if (this.animationType === 'hover') {
      this.mediaCards.forEach((slide, index) => {
        const img = this.images[index];
        if (index <= this.activeSlide) {
          slide.style.clipPath = 'inset(0% 0 0)';
          if (img) img.style.transform = 'translate3d(0, 0, 0)';
        } else {
          slide.style.clipPath = 'inset(100% 0 0)';
          if (img) img.style.transform = 'translate3d(0, 8%, 0)';
        }
      });
    }

    this.updateVerticalSliders();
    this.updateTabSlider();
  }

  // Smooth-scroll to the slide at given index
  scrollToSlide(index) {
    const vh = window.innerHeight;
    const targetScrollY = this.stickyStart + vh * index;
    window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
  }

  // Tab key se button focus hote hi active class turant lag jaaye
  // aur slider bhi sync ho
  bindTabClicks() {
    this.collectionItems.forEach((el, i) => {
      el.addEventListener('focus', () => {
        // Flag set karo taaki scroll event override na kare
        this._focusNavigating = true;
        clearTimeout(this._focusNavTimeout);

        this.activeSlide = i;
        this.updateActiveStates();
        this.updateCardHeightVariable();
        if (this.animationType !== 'hover') {
          this.scrollToSlide(i);
        }

        // Smooth scroll complete hone ke baad flag reset
        this._focusNavTimeout = setTimeout(() => {
          this._focusNavigating = false;
        }, 700);
      });
    });
    this.tabItems.forEach((tab, i) => {
      tab.addEventListener('click', () => {
        this._focusNavigating = true;

        this.activeSlide = i;

        this.updateActiveStates();
        this.updateCardHeightVariable();

        if (this.animationType !== 'hover') {
          this.scrollToSlide(i);
        }
        tab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest',
        });

        setTimeout(() => {
          this._focusNavigating = false;
        }, 700);
      });
    });
  }

  bindHoverEvents() {
    this._hoverTimeout = null;
    this.collectionItems.forEach((el, i) => {
      el.addEventListener('mouseenter', () => {
        if (!this.mediaQuery.matches) return;
        clearTimeout(this._hoverTimeout);
        this._hoverTimeout = setTimeout(() => {
          this.activeSlide = i;
          this.updateActiveStates();
          this.updateCardHeightVariable();
        }, 150);
      });

      el.addEventListener('mouseleave', () => {
        clearTimeout(this._hoverTimeout);
      });
    });
  }

  cacheSliderHeights() {
    const width = window.innerWidth;
    if (this._sliderHeightsCached && this._lastMeasureWidth === width) return;
    this._lastMeasureWidth = width;

    const parentContainer = this.cardInner ? this.cardInner.parentElement : null;
    let oldParentHeight = '';
    // Temporarily remove constraints to get natural unconstrained heights
    if (parentContainer) {
      oldParentHeight = parentContainer.style.height;
      parentContainer.style.height = 'auto';
    }
    this.cardContents.forEach((el) => (el.style.maxHeight = 'none'));

    // Read all offsetHeights once so updateVerticalSliders never triggers reflow
    this._infoHeights = this.infoItems.map((el) => el.offsetHeight);
    this._cardHeights = this.cardContents.map((el) => el.offsetHeight);
    // Pre-cache card content heights for --showcaseCardHeight CSS variable
    this._cardContentHeights = this.cardContents.map((el) => el.getBoundingClientRect().height);

    this._parentExtraHeight = 0;
    if (parentContainer) {
      const styles = getComputedStyle(parentContainer);
      if (styles.boxSizing === 'border-box') {
        this._parentExtraHeight =
          (parseFloat(styles.paddingTop) || 0) +
          (parseFloat(styles.paddingBottom) || 0) +
          (parseFloat(styles.borderTopWidth) || 0) +
          (parseFloat(styles.borderBottomWidth) || 0);
      }
    }

    // Restore constraints
    this.cardContents.forEach((el) => (el.style.maxHeight = ''));
    if (parentContainer) {
      parentContainer.style.height = oldParentHeight;
    }

    this._sliderHeightsCached = true;
  }
  updateTabSlider() {
    if (!this.tabInner || !this.tabWrapper) return;

    const activeTab = this.tabItems[this.activeSlide];
    if (!activeTab) return;

    const wrapperRect = this.tabWrapper.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    let translate = this._tabTranslate || 0;

    if (tabRect.right > wrapperRect.right) {
      translate += tabRect.right - wrapperRect.right;
    }

    if (tabRect.left < wrapperRect.left) {
      translate += tabRect.left - wrapperRect.left;
    }

    this._tabTranslate = translate;

    this.tabInner.style.transform = `translate3d(${-translate}px,0,0)`;
  }
  updateVerticalSliders() {
    if (!this._infoHeights || !this._cardHeights) return;

    let infoTranslate = 0;
    let cardTranslate = 0;

    for (let i = 0; i < this.activeSlide; i++) {
      infoTranslate += this._infoHeights[i] || 0;
      cardTranslate += this._cardHeights[i] || 0;
    }

    if (this.infoList && this.animationType !== 'hover') {
      this.infoList.style.transform = `translate3d(0, -${infoTranslate}px, 0)`;
    }

    if (this.cardInner) {
      this.cardInner.style.transform = `translate3d(0, -${cardTranslate}px, 0)`;
    }
  }
  updateCardHeightVariable() {
    const height = this._cardContentHeights && this._cardContentHeights[this.activeSlide];
    if (height === undefined || !this.cardInner || !this.cardInner.parentElement) return;

    const totalHeight = `${height + (this._parentExtraHeight || 0)}px`;
    if (this._lastCardHeight === totalHeight) return;
    this._lastCardHeight = totalHeight;
    this.cardInner.parentElement.style.setProperty('--showcaseCardHeight', totalHeight);
  }

  onResize() {
    cancelAnimationFrame(this.resizeRaf);

    this.resizeRaf = requestAnimationFrame(() => {
      if (!this.mediaCards) return;

      if (!this.mediaQuery.matches) {
        this.teardownDesktop();
        return;
      }

      if (!this._isDesktopSetup) {
        this.setupDesktop();
      } else {
        layoutRecalcManager.request(this.recalculateLayout);
      }
    });
  }

  disconnectedCallback() {
    clearTimeout(this._hoverTimeout);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.onResize);
    layoutRecalcManager.unregister(this.recalculateLayout);
  }
}
if (!customElements.get('showcase-card')) {
  customElements.define('showcase-card', ShowcaseCard);
}
